import ky from "ky"
import HmacSHA256 from "crypto-js/hmac-sha256"
import EncHex from "crypto-js/enc-hex"
import { clearKeystore, getKeystore } from "./keystore"
import { CLIENT_ID, isProduction } from "../constant"

export class ApiError extends Error {
  constructor(code: number, description?: string, request?: Request) {
    super(`code: ${code}, description: ${description}, request: ${request}`)
    this.name = "ApiError"
    this.code = code
    this.description = description
  }
  readonly code: number
  readonly description?: string
}

export const client = ky.create({
  prefixUrl: isProduction
    ? "https://bond-api.mixin.space/"
    : "https://bond-api.mixin.zone/",
  retry: 0,
  hooks: {
    beforeRequest: [
      async (request) => {
        const keystore = getKeystore();
        if (!keystore || !keystore.token || !keystore.userId) return;

        const now = Date.now();
        // utc
        const timestamp = Math.floor(now / 1000);

        const payload = [
          keystore.userId,
          timestamp,
          request.method,
          request.url.replace(new URL(request.url).origin, ""),
          await request.clone().text(),
        ];

        const sig = HmacSHA256(payload.join(""), keystore.token).toString(
          EncHex
        );

        request.headers.set(
          "Authorization",
          "Bearer " +
            btoa(
              JSON.stringify({
                u: keystore.userId,
                t: timestamp,
                s: sig,
              })
            )
              .replace(/\+/g, "-")
              .replace(/\//g, "_")
              .replace(/=+$/, "")
        );
      },
    ],
    afterResponse: [
      async (request, _options, response) => {
        const json = await response.json();
        if (json && Object.keys(json).length === 0) {
          return new Response("{}");
        }
        if (
          json &&
          typeof json === "object" &&
          "data" in json &&
          json.data !== undefined
        ) {
          return new Response(JSON.stringify(json.data));
        }

        const error =
          (json && typeof json === "object" && "error" in json && json.error) ||
          json;
        if (typeof error === "object" && error !== null && "code" in error) {
          const code = (error as { code?: number }).code;
          if (code === 401) {
            clearKeystore();
            window.location.href = "/auth";
            return new Response("{}");
          }
          throw new ApiError(
            code ?? 500,
            (error as { description?: string }).description,
            request
          );
        }
        throw new ApiError(500, JSON.stringify(error), request);
      },
    ],
    beforeError: [],
  },
});

export const authWithMixin = () => {
  location.replace(`https://mixin.one/oauth/authorize?client_id=${CLIENT_ID}&scope=PROFILE:READ+ASSETS:READ&response_type=code`)
}
